import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import ProfileTab from './components/ProfileTab';
import CompanyTab from './components/CompanyTab';
import TeamTab from './components/TeamTab';
import CustomFieldsTab from './components/CustomFieldsTab';
import PipelineTab from './components/PipelineTab';
import SubscriptionTab from './components/SubscriptionTab';
import ApiKeyTab from './components/ApiKeyTab';
import WebsiteIntegrationTab from './components/WebsiteIntegrationTab';
import AdIntegrationsTab from './components/AdIntegrationsTab';
import ChangeBusinessTypeTab from './components/ChangeBusinessTypeTab';
import SMSNotificationsTab from './components/SMSNotificationsTab';

const Settings = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const [oauthProvider, setOauthProvider] = useState(null);
  const [oauthCode, setOauthCode] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Handle OAuth redirect back to settings
  useEffect(() => {
    const params = new URLSearchParams(location?.search);
    const tab = params?.get('tab');
    const oauth = params?.get('oauth');
    const code = params?.get('code');
    const oauthStatus = params?.get('status');

    if (tab === 'integrations') {
      setActiveTab('integrations');
    }
    // Legacy: code exchanged here (Meta flow still uses this path)
    if (oauth && code) {
      setOauthProvider(oauth);
      setOauthCode(code);
      // Clean URL without reloading
      const cleanUrl = window.location?.pathname + '?tab=integrations';
      window.history?.replaceState({}, '', cleanUrl);
    }
    // New: Google Ads code was already exchanged in auth-callback, just pass status
    if (oauth === 'google' && oauthStatus) {
      setOauthProvider('google_status');
      setOauthCode(oauthStatus); // reuse oauthCode field to pass status string
      const cleanUrl = window.location?.pathname + '?tab=integrations';
      window.history?.replaceState({}, '', cleanUrl);
    }
  }, [location?.search]);

  const handleSidebarToggle = () => setIsSidebarOpen(!isSidebarOpen);
  const handleSidebarClose = () => setIsSidebarOpen(false);

  const tabs = [
    { id: 'profile', label: 'Profile', icon: 'User', description: 'Personal information and preferences' },
    { id: 'company', label: 'Company', icon: 'Building2', description: 'Organization details and branding' },
    { id: 'team', label: 'Team & Roles', icon: 'Users', description: 'Manage team members and permissions' },
    { id: 'fields', label: 'Custom Fields', icon: 'Settings', description: 'Create and manage custom fields' },
    { id: 'pipeline', label: 'Pipeline', icon: 'GitBranch', description: 'Configure sales pipeline and automation' },
    { id: 'subscription', label: 'Subscription', icon: 'CreditCard', description: 'Plan, billing, and payment methods' },
    { id: 'apikeys', label: 'API Keys', icon: 'Key', description: 'Manage API keys for external integrations' },
    { id: 'website', label: 'Website Integration', icon: 'Globe', description: 'Connect your website and capture leads automatically' },
    { id: 'integrations', label: 'Integrations', icon: 'Plug', description: 'Connect Google Ads and Meta for AI Marketing' },
    { id: 'sms', label: 'SMS Notifications', icon: 'MessageSquare', description: 'Send SMS alerts to clients via Twilio' },
    { id: 'business_type', label: 'Business Type', icon: 'RefreshCw', description: 'Change your business niche and reset CRM configuration' },
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'profile': return <ProfileTab />;
      case 'company': return <CompanyTab />;
      case 'team': return <TeamTab />;
      case 'fields': return <CustomFieldsTab />;
      case 'pipeline': return <PipelineTab />;
      case 'subscription': return <SubscriptionTab />;
      case 'apikeys': return <ApiKeyTab />;
      case 'website': return <WebsiteIntegrationTab />;
      case 'integrations': return <AdIntegrationsTab oauthProvider={oauthProvider} oauthCode={oauthCode} onOAuthHandled={() => { setOauthProvider(null); setOauthCode(null); }} />;
      case 'sms': return <SMSNotificationsTab />;
      case 'business_type': return <ChangeBusinessTypeTab />;
      default: return <ProfileTab />;
    }
  };

  return (
    <>
      <Helmet>
        <title>Settings - Wilcart CRM</title>
        <meta name="description" content="Configure your Wilcart CRM workspace settings." />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header onMenuToggle={handleSidebarToggle} isSidebarOpen={isSidebarOpen} />
        <Sidebar isOpen={isSidebarOpen} onClose={handleSidebarClose} />
        <main className="lg:ml-64 pt-24 lg:pt-28">
          <div className="p-4 lg:p-8">
            <div className="mb-8">
              <div className="flex items-center space-x-4 mb-2">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Settings" size={24} className="text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl lg:text-3xl font-bold text-foreground">Settings</h1>
                  <p className="text-muted-foreground">Configure your Wilcart CRM workspace and preferences</p>
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="border-b border-border">
                <nav className="flex flex-wrap gap-1 px-4 pt-3 pb-0">
                  {tabs?.map((tab) => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center space-x-2 py-2.5 px-3 mb-3 rounded-lg font-medium text-sm transition-smooth whitespace-nowrap
                        ${activeTab === tab?.id
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`}
                    >
                      <Icon name={tab?.icon} size={16} />
                      <span>{tab?.label}</span>
                    </button>
                  ))}
                </nav>
              </div>

              <div className="p-6 lg:p-8">
                <div className="mb-6">
                  <div className="flex items-center space-x-3">
                    <Icon name={tabs?.find(t => t?.id === activeTab)?.icon || 'Settings'} size={20} className="text-primary" />
                    <div>
                      <h2 className="text-lg font-semibold text-card-foreground">{tabs?.find(t => t?.id === activeTab)?.label}</h2>
                      <p className="text-sm text-muted-foreground">{tabs?.find(t => t?.id === activeTab)?.description}</p>
                    </div>
                  </div>
                </div>
                {renderTabContent()}
              </div>
            </div>

            <div className="mt-8 bg-muted rounded-xl p-6">
              <div className="flex items-start space-x-4">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="HelpCircle" size={20} className="text-primary" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-card-foreground mb-2">Need Help?</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    If you need assistance with any settings, check out our documentation or contact support.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    <button
                      onClick={() => navigate('/documentation')}
                      className="inline-flex items-center px-4 py-2 bg-background border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-smooth"
                    >
                      <Icon name="Book" size={16} className="mr-2" />
                      View Documentation
                    </button>
                    <button
                      onClick={() => navigate('/contact-support')}
                      className="inline-flex items-center px-4 py-2 bg-background border border-border rounded-lg text-sm font-medium text-foreground hover:bg-muted transition-smooth"
                    >
                      <Icon name="MessageCircle" size={16} className="mr-2" />
                      Contact Support
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default Settings;